import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from "framer-motion";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";

const EmoticonRow = ({
  src,
  text,
  idx,
}: {
  src: string;
  text: ReactNode;
  idx: number;
  emoticon: string;
}) => (
  <div className="flex flex-row gap-2 justify-center">
    {idx % 2 === 0 && (
      <img loading="lazy" src={src} alt="emoticon" width={32} className="h-auto self-start" />
    )}
    <div
      style={{
        width: "clamp(235px, 60vw, 540px)",
        boxShadow: "0 1.813px 19.07px 0 rgba(48, 75, 109, 0.20)",
      }}
      className={`mt-[20px] self-end py-7
    p-[22px] ${
      idx % 2 === 0 ? `rounded-r-[32px] rounded-bl-[32px]` : `rounded-l-[32px] rounded-br-[32px]`
    } flex flex-row items-center gap-[20px]
    border-[3px] border-transparent
    [background-origin:border-box]
    [background-clip:padding-box,border-box]
    bg-[linear-gradient(#fff,#fff),linear-gradient(90deg,#0079FF_0%,#00FF44_100%)]`}
    >
      {text}
    </div>
    {idx % 2 === 1 && (
      <img loading="lazy" src={src} alt="emoticon" width={32} className="h-auto self-start" />
    )}
  </div>
);

const MobileAnimatedRow = ({ children }: { children: ReactNode }) => {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 8) setArmed(true); // 8px 정도만 내려도 OK
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      animate={armed ? undefined : { opacity: 0, y: 22 }}
      whileInView={armed ? { opacity: 1, y: 0 } : undefined}
      viewport={{ once: true, amount: 0.5, margin: "0px 0px -40% 0px" }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

type BubbleItem = {
  id: string;
  img: string; //  추가
  top: string;
  left?: string;
  right?: string;
  width?: string; // 기존 유지하되, 이제 "기준값"으로만 써도 됨(선택)
  height?: string;
};

const getClampSize = (value?: string) => {
  if (!value) return undefined;
  const numeric = Number(value);
  return `clamp(calc(${numeric}px * 0.7), calc(${numeric} * 100vw / 1440), ${numeric}px)`;
};

const bubbles: BubbleItem[] = [
  {
    id: "b1",
    img: "/assets/images/icon/bubble.webp",
    top: "0px",
    left: "16px",
    width: "766",
    height: "161",
  },
  {
    id: "b2",
    img: "/assets/images/icon/bubble2.png",
    top: "169px",
    right: "16px",
    width: "720",
    height: "169",
  },
  {
    id: "b3",
    img: "/assets/images/icon/bubble3.png",
    top: "346px",
    left: "112px",
    width: "650",
    height: "180",
  },
  {
    id: "b4",
    img: "/assets/images/icon/bubble4.png",
    top: "534px",
    right: "16px",
    width: "796",
    height: "179",
  },
  {
    id: "b5",
    img: "/assets/images/icon/bubble5.png",
    top: "721px",
    left: "36px",
    width: "746",
    height: "179",
  },
];

export const Section1 = () => {
  const isMobile = useResponsiveMobile();
  const sectionRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  //  라인 진행도 기준을 "라인 래퍼"로 잡기
  const lineWrapRef = useRef<HTMLDivElement | null>(null);
  const lineWrapRef2 = useRef<HTMLDivElement | null>(null);
  const [bubblesAnimated, setBubblesAnimated] = useState(false);
  const [headlineAnimated, setHeadlineAnimated] = useState(false);

  const bubbleDelays = useMemo(() => {
    return bubbles.reduce<Record<string, number>>((acc, b, idx) => {
      acc[b.id] = 200 + idx * 220;
      return acc;
    }, {});
  }, []);

  const bubbleLayout = useMemo(() => {
    return bubbles.reduce<BubbleItem[]>((acc, b, idx) => {
      if (idx === 0) {
        acc.push({ ...b, top: "0px" });
        return acc;
      }

      const prev = acc[idx - 1];
      const prevHeight = getClampSize(prev.height) ?? "0px";
      const top = `calc(${prev.top} + ${prevHeight} + 8px)`;
      acc.push({ ...b, top });
      return acc;
    }, []);
  }, []);
  const bubbleContainerHeight = useMemo(() => {
    if (bubbleLayout.length === 0) return "0px";
    const last = bubbleLayout[bubbleLayout.length - 1];
    const lastHeight = getClampSize(last.height) ?? "0px";
    return `calc(${last.top} + ${lastHeight} + 80px)`;
  }, [bubbleLayout]);

  // =========================================================
  //  라인 채움: "라인이 화면에 들어오는 것"을 기준으로 progress 계산
  // =========================================================
  const { scrollYProgress: lineProgress } = useScroll({
    target: lineWrapRef,
    /**
     * offset 의미(중요):
     * - "start 70%": 라인 래퍼의 top이 viewport 70% 지점에 닿을 때 progress=0 (조금 늦게 시작)
     * - "end 30%":   라인 래퍼의 bottom이 viewport 30% 지점에 닿을 때 progress=1 (조금 늦게 100%)
     *
     * -> 이 구간이 길수록 "천천히" 채워집니다.
     */
    offset: ["start 50%", "end 50%"],
  });

  //  0~1 값을 motionValue로 즉각 반영
  const fill = useMotionValue(0);
  useMotionValueEvent(lineProgress, "change", (p) => {
    fill.set(Math.max(0, Math.min(1, p)));
  });

  //  height(%)
  const fillHeight = useTransform(fill, (v) => `${v * 100}%`);

  // =========================================================
  //  말풍선/텍스트 등장 트리거 (기존 유지)
  // =========================================================
  useEffect(() => {
    if (bubblesAnimated) return;

    let ticking = false;

    const check = () => {
      const el = triggerRef.current;
      if (!el) return;

      const elementY = el.getBoundingClientRect().top + window.scrollY;
      const viewportBottom = window.scrollY + window.innerHeight;

      if (viewportBottom >= elementY) {
        setBubblesAnimated(true);
        window.removeEventListener("scroll", onScroll);
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        check();
        ticking = false;
      });
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", check);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", check);
    };
  }, [bubblesAnimated]);
  useEffect(() => {
    if (headlineAnimated) return;

    let ticking = false;

    const check = () => {
      const el = lineWrapRef2.current;
      if (!el) return;

      const elementY = el.getBoundingClientRect().top + window.scrollY;
      const viewportBottom = window.scrollY + window.innerHeight;

      //  화면 하단이 요소 top을 지나면 등장
      if (viewportBottom >= elementY) {
        // +100은 여유(원하면 0으로)
        setHeadlineAnimated(true);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", check);
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        check();
        ticking = false;
      });
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", check);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", check);
    };
  }, [headlineAnimated]);
  return (
    <section ref={sectionRef} className="flex flex-col h-full pt-[80px]">
      {isMobile ? (
        <div className="flex flex-col w-full px-4 gap-4">
          {[
            {
              emoticon: "😐",
              src: "/assets/images/emoticon/emoticon.png",
              text: (
                <span className="font-normal" style={{ fontSize: "clamp(14px, 2.4vw, 26px)" }}>
                  PG마다 엑셀 따로.. 정산할 때마다 vlookup 지옥이에요
                </span>
              ),
            },
            {
              emoticon: "😮‍💨",
              src: "/assets/images/emoticon/emoticon2.png",
              text: (
                <span className="font-normal" style={{ fontSize: "clamp(14px, 2.4vw, 26px)" }}>
                  숫자 하나 틀릴까봐 정산 <br />
                  마감 날엔 항상 불안해요
                </span>
              ),
            },
            {
              src: "/assets/images/emoticon/emoticon3.png",
              text: (
                <span className="font-normal" style={{ fontSize: "clamp(14px, 2.4vw, 26px)" }}>
                  거래는 늘었는데 관리가 <br />
                  따라오질 않네요
                </span>
              ),
              emoticon: "😔",
            },
            {
              emoticon: "😵",
              src: "/assets/images/emoticon/emoticon4.png",
              text: (
                <span className="font-normal" style={{ fontSize: "clamp(14px, 2.4vw, 26px)" }}>
                  시스템은 필요한데..뭘, 어디서
                  <br />
                  부터 만들어야할 지 모르겠어요
                </span>
              ),
            },
            {
              emoticon: "🫠",
              src: "/assets/images/emoticon/emoticon5.png",
              text: (
                <span className="font-normal" style={{ fontSize: "clamp(14px, 2.4vw, 26px)" }}>
                  가맹점 관리를 잘하고 싶은데, <br />
                  방법을 모르겠어요
                </span>
              ),
            },
          ].map((item, idx) => (
            <MobileAnimatedRow key={item.src}>
              <EmoticonRow emoticon={item.emoticon} src={item.src} text={item.text} idx={idx} />
            </MobileAnimatedRow>
          ))}
        </div>
      ) : (
        <div
          className="relative w-full h-full max-w-[1466px] mx-auto"
          style={{ minHeight: bubbleContainerHeight }}
        >
          <div
            ref={triggerRef}
            className="absolute left-0 right-0"
            style={{ top: "55%", height: "1px" }}
            aria-hidden="true"
          />

          {bubbleLayout.map((b) => (
            <div
              key={b.id}
              className={[
                "absolute will-change-transform will-change-opacity",
                "transition-[opacity,transform] duration-[1200ms] ease-out",
                bubblesAnimated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12",
              ].join(" ")}
              style={{
                top: b.top,
                left: b.left,
                right: b.right,
                transitionDelay: `${bubbleDelays[b.id]}ms`,
                filter: "drop-shadow(0 1.813px 19.07px rgba(48, 75, 109, 0.20))",
              }}
            >
              <img
                src={b.img}
                alt="bubble"
                draggable={false}
                loading="lazy"
                className="block select-none pointer-events-none"
                style={{
                  width: getClampSize(b.width),
                  height: getClampSize(b.height) ?? "auto",
                }}
              />
            </div>
          ))}
        </div>
      )}

      {isMobile ? (
        <motion.div
          className="flex flex-col items-center justify-center mt-[150px]"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
        >
          <span className="text-center text-[36px] font-bold text-[#121319]">
            복잡하고 어려운 <br />
            파트너 관리, <br />더 이상 <span className="text-[#0079FF]">비효율</span>에 <br />
            매달리지 마세요
          </span>
          <img
            loading="lazy"
            src="/assets/images/home/image.webp"
            alt=""
            draggable={false}
            className="w-[min(90vw,600px)] h-auto"
          />
        </motion.div>
      ) : (
        <div
          ref={lineWrapRef2}
          className={[
            "flex flex-col items-center justify-center",
            "transition-all duration-[1200ms] ease-out",
            headlineAnimated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10",
          ].join(" ")}
          style={{ transitionDelay: "900ms" }}
        >
          <span className="text-center text-display font-bold text-[#121319]">
            복잡하고 어려운 파트너 관리, <br />더 이상{" "}
            <span className="text-[#0079FF]">비효율</span>에 매달리지 마세요
          </span>
          <img
            loading="lazy"
            src="/assets/images/home/image.webp"
            alt=""
            draggable={false}
            className="w-[min(70vw,838px)] h-auto"
          />
        </div>
      )}

      {/*  라인 래퍼를 target으로 잡아야, 보일 때부터 채워집니다 */}
      <div ref={lineWrapRef} className="flex justify-center">
        <div
          className="relative overflow-hidden"
          style={{
            width: "4px",
            height: "203px",
            background: "#C9C9C9",
            borderRadius: "999px",
          }}
        >
          <motion.div
            className="absolute top-0 bottom-0 w-full"
            style={{
              height: fillHeight,
              backgroundColor: "#0068FF",
              borderRadius: "999px",
            }}
          />
        </div>
      </div>
    </section>
  );
};
